<template>
  <div
    class="app-sidebar-wrap noPrint"
    :class="{ 'app-sidebar-wrap--open-mobile': mobileOpen }"
  >
    <div
      class="app-sidebar-backdrop d-lg-none"
      v-show="mobileOpen"
      @click="$emit('close-mobile')"
      aria-hidden="true"
    />
    <aside
      class="app-sidebar"
      :class="{
        'app-sidebar--collapsed': effectiveCollapsed && !mobileOpen,
        'app-sidebar--close-mobile': !mobileOpen,
      }"
    >
      <div
        class="app-sidebar__header"
        :class="{ 'app-sidebar__header--drill': drillUi.showBack }"
      >
        <template v-if="drillUi.showBack">
          <div class="app-sidebar__header-drill-top">
            <div
              class="app-sidebar__drill-heading text-main"
              ref="titleEl"
              v-tooltip="isTitleTruncated ? drillUi.title : null"
            >
              {{ drillUi.title }}
            </div>
            <button
              type="button"
              class="app-sidebar__pin btn btn-link text-main p-0"
              @click="toggleCollapsed"
              :title="collapsed ? 'Expand menu' : 'Collapse menu'"
            >
              <i
                class="fa"
                :class="
                  effectiveCollapsed && !mobileOpen
                    ? 'fa-angle-double-right'
                    : 'fa-angle-double-left'
                "
                aria-hidden="true"
              />
            </button>
          </div>
          <hr class="app-sidebar__hr" aria-hidden="true" />
          <button
            type="button"
            class="app-sidebar__back app-sidebar__back--drill btn btn-link"
            aria-label="Back to main menu"
            @click="handleDrillBack"
          >
            <i
              class="fa fa-arrow-left app-sidebar__back-icon"
              aria-hidden="true"
            />
            <span class="app-sidebar__back-label">Back</span>
          </button>
          <hr class="app-sidebar__hr" aria-hidden="true" />
        </template>
        <template v-else>
          <div
            class="app-sidebar__title text-main"
            v-tooltip="isTitleTruncated ? headerDisplayTitle : null"
            ref="titleEl"
          >
            {{ headerDisplayTitle }}
          </div>
          <button
            type="button"
            class="app-sidebar__pin btn btn-link text-main p-0"
            @click="toggleCollapsed"
            :title="collapsed ? 'Expand menu' : 'Collapse menu'"
          >
            <i
              class="fa"
              :class="
                collapsed && !mobileOpen
                  ? 'fa-angle-double-right'
                  : 'fa-angle-double-left'
              "
              aria-hidden="true"
            />
          </button>
        </template>
      </div>
      <div class="app-sidebar__body">
        <component
          :is="navComponent"
          ref="roleNav"
          :collapsed="effectiveCollapsed"
          @drill-change="onDrillChange"
          @navigated="$emit('close-mobile')"
        />
      </div>
    </aside>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SidebarNavAdmin from "./sidebar/SidebarNavAdmin.vue";
import SidebarNavProvider from "./sidebar/SidebarNavProvider.vue";
import SidebarNavPatient from "./sidebar/SidebarNavPatient.vue";
import SidebarNavReferral from "./sidebar/SidebarNavReferral.vue";

const STORAGE_KEY = "sidebar_collapsed";

export default {
  name: "AppSidebar",
  components: {
    SidebarNavAdmin,
    SidebarNavProvider,
    SidebarNavPatient,
    SidebarNavReferral,
  },
  props: {
    mobileOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close-mobile"],
  data() {
    return {
      collapsed: false,
      drillUi: { showBack: false, title: "" },
      isTitleTruncated: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      companyName: (state) => (state.settings.companyProfile || {}).name || "",
    }),
    navComponent() {
      const u = this.user;
      if (!u) return null;
      if (u.isAdmin || u.isManagingAdmin) return "SidebarNavAdmin";
      if (u.isProvider) return "SidebarNavProvider";
      if (u.isPatient) return "SidebarNavPatient";
      if (u.isReferer) return "SidebarNavReferral";
      return null;
    },
    effectiveCollapsed() {
      // Keep the collapsed appearance when `drillUi.showBack` is true
      // unless the sidebar is open on mobile. This prevents route-driven
      // drill changes from forcing the menu open when the user has it collapsed.
      return this.collapsed && (!this.drillUi.showBack || !this.mobileOpen);
    },
    headerDisplayTitle() {
      if (this.drillUi.showBack && this.drillUi.title) {
        return this.drillUi.title;
      }
      return this.companyName || "Menu";
    },
  },
  mounted() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === "1" || raw === "true") {
        this.collapsed = true;
      }
    } catch {
      /* ignore */
    }
    this.syncSidebarWidthCss();
    window.addEventListener("resize", this.checkTitleTruncation);
    this.$nextTick(this.checkTitleTruncation);
  },
  beforeUnmount() {
    document.documentElement.style.removeProperty("--app-sidebar-width");
    window.removeEventListener("resize", this.checkTitleTruncation);
  },
  watch: {
    headerDisplayTitle() {
      this.$nextTick(this.checkTitleTruncation);
    },
    drillUi: {
      deep: true,
      handler() {
        this.$nextTick(this.checkTitleTruncation);
      },
    },
    effectiveCollapsed() {
      this.syncSidebarWidthCss();
    },
  },
  methods: {
    syncSidebarWidthCss() {
      const w = this.effectiveCollapsed ? 66 : 260;
      document.documentElement.style.setProperty(
        "--app-sidebar-width",
        `${w}px`
      );
    },
    checkTitleTruncation() {
      const el = this.$refs.titleEl;
      if (!el) {
        this.isTitleTruncated = false;
        return;
      }
      this.isTitleTruncated = el.scrollWidth > el.clientWidth;
    },
    toggleCollapsed() {
      if (this.mobileOpen) {
        this.collapsed = true; // keep collapsed state in sync for when mobile view is closed
        this.drillUi.showBack = false; // always collapse when in mobile view
        this.handleDrillBack(); // if collapsing, also go back to main menu
        this.$emit("close-mobile");
      } else {
        this.collapsed = !this.collapsed;
        if (this.collapsed) {
          this.handleDrillBack(); // if collapsing, also go back to main menu
        }
      }
      try {
        localStorage.setItem(STORAGE_KEY, this.collapsed ? "1" : "0");
      } catch {
        /* ignore */
      }
    },
    onDrillChange(payload) {
      this.drillUi = {
        showBack: !!(payload && payload.showBack),
        title: (payload && payload.title) || "",
      };
      if (this.drillUi.showBack) {
        // If the drill was triggered by a user click, always expand.
        // Otherwise only auto-expand when the sidebar is visible (mobile) or already expanded.
        if (
          (payload && payload.userInitiated) ||
          this.mobileOpen ||
          !this.collapsed
        ) {
          this.collapsed = false;
        }
      }
    },
    handleDrillBack() {
      const nav = this.$refs.roleNav;
      if (nav && typeof nav.goBackDrill === "function") {
        nav.goBackDrill();
      }
    },
  },
};
</script>
